import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto"; // Placeholder for gallery
import VerifiedIcon from "@mui/icons-material/Verified";
import { toast } from "react-toastify";
import { changePasswordApi } from "../../services/AuthServices";
import Storage from "../../utils/HandelLocalStorage";
import { ApiEndPoints } from "../../apis/ApiEndPoints";
import { getRecord } from "../../apis/services/CommonApiService";
import { useNavigate } from "react-router-dom";
// Removed import DUMMY DATA

// ---------------------- STYLES --------------------------
const useStyles = makeStyles({
  root: {
    padding: "30px",
    backgroundColor: "#f4f6f8",
    minHeight: "100vh",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  pageTitle: {
    fontWeight: "700",
    color: "#1a2027",
    fontSize: "28px",
  },
  actionBtn: {
    textTransform: "none",
    fontWeight: "600",
    boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
    borderRadius: "8px",
    padding: "8px 20px",
  },
  primaryBtn: {
    background: "linear-gradient(135deg, #255480 0%, #1e456e 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #1e456e 0%, #255480 100%)",
    },
  },
  secondaryBtn: {
    backgroundColor: "#fff",
    color: "#255480",
    border: "1px solid #255480",
    marginRight: "15px",
    "&:hover": {
      backgroundColor: "#eef4fa",
    },
  },
  card: {
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
    border: "none",
    overflow: "hidden",
  },
  heroCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
    display: "flex",
    alignItems: "center",
    padding: "20px",
  },
  avatar: {
    width: "100px",
    height: "100px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
    border: "4px solid #fff",
    marginRight: "25px",
  },
  establishmentName: {
    fontWeight: "700",
    fontSize: "24px",
    color: "#255480",
    marginBottom: "5px",
  },
  tagline: {
    color: "#637381",
    fontSize: "15px",
    marginBottom: "10px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1a2027",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  infoItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "18px",
  },
  infoIcon: {
    color: "#255480",
    marginRight: "15px",
    marginTop: "3px",
    fontSize: "22px",
  },
  infoLabel: {
    color: "#637381",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "2px",
    textTransform: "uppercase",
  },
  infoValue: {
    color: "#212b36",
    fontSize: "15px",
    fontWeight: "500",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  chip: {
    borderRadius: "8px",
    fontWeight: "500",
    backgroundColor: "#eef4fa",
    color: "#255480",
    border: "1px solid #dce7f2",
  },
  galleryImage: {
    width: "100%",
    height: "120px",
    objectFit: "cover",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "80vh",
  },
  verifiedBadge: {
    color: "#00ab55",
    marginLeft: "8px",
    verticalAlign: "middle",
  },
});
const checkIsOpenNow = (workingHoursDetails) => {
  if (!workingHoursDetails || !Array.isArray(workingHoursDetails)) return false;

  const now = new Date();
  const currentDay = now.getDay();
  const prevDay = currentDay === 0 ? 6 : currentDay - 1;

  const todaySchedule = workingHoursDetails.find(hours => parseInt(hours.day_of_week) === currentDay);
  const yesterdaySchedule = workingHoursDetails.find(hours => parseInt(hours.day_of_week) === prevDay);

  const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Check if open from yesterday's night shift
  if (yesterdaySchedule && !yesterdaySchedule.is_day_off) {
    if (yesterdaySchedule.start_time > yesterdaySchedule.end_time) {
      if (currentTimeStr <= yesterdaySchedule.end_time) {
        return true;
      }
    }
  }

  // Check if open from today's shift
  if (todaySchedule && !todaySchedule.is_day_off) {
    if (todaySchedule.start_time <= todaySchedule.end_time) {
      if (currentTimeStr >= todaySchedule.start_time && currentTimeStr <= todaySchedule.end_time) {
        return true;
      }
    } else {
      if (currentTimeStr >= todaySchedule.start_time) {
        return true;
      }
    }
  }

  return false;
};

function Profile() {
  const classes = useStyles();
  const navigate = useNavigate(); // Hook for navigation
  const [user, setUser] = useState();
  const [establishmentDetail, setEstablishmentDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // ⭐ CHANGE PASSWORD STATE
  const [openChangePass, setOpenChangePass] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ---------------------- FETCH USER & ESTABLISHMENT --------------------------
  useEffect(() => {
    const getUserInfo = async () => {
      const params = await Storage.retrieveItem("userDetails");
      if (params) {
        const userData = JSON.parse(params);
        setUser(userData);
        if (userData.establishment_id) {
          fetchEstablishment(userData.establishment_id);
        } else {
          // Fallback if no establishment ID is found in user details
          setEstablishmentDetail(null);
        }
      } else {
        // Fallback if no user details at all (e.g. fresh reload without login)
        setEstablishmentDetail(null);
      }
    };
    getUserInfo();
  }, []);

  const fetchEstablishment = async (id) => {
    try {
      setLoading(true);
      const result = await getRecord(
        id,
        ApiEndPoints.ESTABLISHMENT_RESOURCE_ROUTE
      );
      if (result.success || result.status === 200) {
        setEstablishmentDetail(result.data || result);
      } else {
        // toast.error("Failed to fetch establishment details"); // Suppress error for now
        console.log("Failed to fetch establishment details");
        setEstablishmentDetail(null);
      }
    } catch (error) {
      console.error("Error fetching establishment:", error);
      // toast.error("Error loading details");
      setEstablishmentDetail(null);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- PASSWORD FUNCTIONS --------------------------
  const handleOpenPassword = () => {
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setOpenChangePass(true);
  };

  const handlePasswordSubmit = async () => {
    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      toast.error("All fields required");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
    if (!passwordRegex.test(passwordForm.newPassword)) {
      toast.error("New password must be at least 12 characters and contain uppercase, lowercase, number, and special character");
      return;
    }
    try {
      const response = await changePasswordApi({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword,
      });
      toast.success(response.message);
      setOpenChangePass(false);
    } catch (error) {
      toast.error("Password change failed");
    }
  };

  // ---------------------- NAVIGATION --------------------------
  const handleViewProfessionals = () => {
    navigate("/professionals");
  };

  // ---------------------- RENDER --------------------------
  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={60} thickness={4} style={{ color: "#255480" }} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* HEADER */}
      <div className={classes.header}>
        <div>
          <Typography className={classes.pageTitle}>Establishment Profile</Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your establishment details and settings
          </Typography>
        </div>
        <div>

          {/* ⭐ NEW VIEW PROFESSIONALS BUTTON */}
          <Button
            className={`${classes.actionBtn} ${classes.secondaryBtn}`}
            variant="outlined"
            startIcon={<GroupIcon />}
            onClick={handleViewProfessionals}
          >
            View Professionals
          </Button>

          <Button
            className={`${classes.actionBtn} ${classes.primaryBtn}`}
            variant="contained"
            disabled={!user?.establishment_id}
            startIcon={<EditIcon />}
            onClick={() => navigate(`/establishments/edit/${user?.establishment_id}`)}
            style={{ marginRight: "15px" }}
          >
            Edit Establishment
          </Button>

          <Button
            className={`${classes.actionBtn} ${classes.primaryBtn}`}
            variant="contained"
            disabled={!user?.establishment_id}
            startIcon={<AccessTimeIcon />}
            onClick={() => navigate(`/establishments/manage-hours/${user?.establishment_id}`)}
            style={{ marginRight: "15px" }}
          >
            Manage Hours
          </Button>

          <Button
            className={`${classes.actionBtn} ${classes.primaryBtn}`}
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleOpenPassword}
          >
            Change Password
          </Button>
        </div>
      </div>

      {establishmentDetail ? (
        <Grid container spacing={3}>
          {/* HERO SECTION */}
          <Grid item xs={12}>
            <Card className={`${classes.card} ${classes.heroCard}`}>
              <Avatar
                src={establishmentDetail.primary_photo}
                alt={establishmentDetail.name}
                className={classes.avatar}
                variant="rounded"
              >
                {establishmentDetail.name?.charAt(0)}
              </Avatar>
              <div>
                <Typography className={classes.establishmentName}>
                  {establishmentDetail.name}
                  {establishmentDetail.healineVerified && (
                    <VerifiedIcon className={classes.verifiedBadge} />
                  )}
                </Typography>
                <Typography className={classes.tagline}>
                  {establishmentDetail.about || "No description available."}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <Chip
                    label={
                      establishmentDetail.active_status ? "Active" : "Inactive"
                    }
                    color={establishmentDetail.active_status ? "success" : "default"}
                    size="small"
                  />
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={
                      establishmentDetail.is_24_by_7_working == 1
                        ? "24*7"
                        : (checkIsOpenNow(establishmentDetail.workingHoursDetails) ? "Open Now" : "Closed Now")
                    }
                    variant="outlined"
                    size="small"
                    color={
                      establishmentDetail.is_24_by_7_working == 1
                        ? "default"
                        : (checkIsOpenNow(establishmentDetail.workingHoursDetails) ? "success" : "error")
                    }
                  />
                </Box>
              </div>
            </Card>
          </Grid>

          {/* LEFT COLUMN: CONTACT & ADDRESS */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={3} direction="column">
              <Grid item>
                <Card className={classes.card}>
                  <CardContent>
                    <Typography className={classes.sectionTitle}>
                      <ContactInfoIconWrapper icon={<BusinessIcon />} />
                      Contact Information
                    </Typography>
                    <InfoRow
                      icon={<EmailIcon />}
                      label="Email"
                      value={establishmentDetail.email}
                      classes={classes}
                    />
                    <InfoRow
                      icon={<PhoneIcon />}
                      label="Phone"
                      value={establishmentDetail.contact_number}
                      classes={classes}
                    />
                    <InfoRow
                      icon={<VerifiedIcon />} // Using VerifiedIcon as a placeholder for License
                      label="License No."
                      value={establishmentDetail.licence_no}
                      classes={classes}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item>
                <Card className={classes.card}>
                  <CardContent>
                    <Typography className={classes.sectionTitle}>
                      <ContactInfoIconWrapper icon={<LocationOnIcon />} />
                      Location
                    </Typography>
                    <Typography
                      variant="body1"
                      style={{ marginBottom: "15px", color: "#212b36" }}
                    >
                      {establishmentDetail.address}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography className={classes.infoLabel}>City</Typography>
                        <Typography className={classes.infoValue}>
                          {establishmentDetail.cityInfo?.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography className={classes.infoLabel}>Zone</Typography>
                        <Typography className={classes.infoValue}>
                          {establishmentDetail.zoneInfo?.name}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Button
                      variant="text"
                      color="primary"
                      href={`https://maps.google.com/?q=${establishmentDetail.latitude},${establishmentDetail.longitude}`}
                      target="_blank"
                      startIcon={<LocationOnIcon />}
                      style={{ marginTop: "15px", paddingLeft: 0 }}
                    >
                      View on Google Maps
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* WORKING HOURS SECTION MOVED TO LEFT COLUMN */}
              <Grid item>
                <Card className={classes.card}>
                  <CardContent>
                    <Typography className={classes.sectionTitle}>
                      <ContactInfoIconWrapper icon={<AccessTimeIcon />} />
                      Working Hours
                    </Typography>
                    <Grid container spacing={2}>
                      {establishmentDetail.workingHoursDetails?.map((hours, index) => {
                        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                        return (
                          <Grid item xs={12} key={index}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <Typography variant="body2" style={{ fontWeight: 600, color: '#454f5b' }}>
                                {days[hours.day_of_week]}
                              </Typography>
                              <Typography variant="body2" style={{ color: hours.is_day_off ? '#d32f2f' : '#212b36' }}>
                                {hours.is_day_off ? "Day Off" : `${hours.start_time} - ${hours.end_time}`}
                              </Typography>
                            </div>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* RIGHT COLUMN: DETAILS */}
          <Grid item xs={12} md={8}>
            <Card className={classes.card} style={{ marginBottom: "24px" }}>
              <CardContent>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Establishment Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography className={classes.infoLabel}>Type</Typography>
                        <Typography className={classes.infoValue}>
                          {establishmentDetail.establishmentTypeInfo?.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography className={classes.infoLabel}>Sub-Type</Typography>
                        <Typography className={classes.infoValue}>
                          {establishmentDetail.establishmentSubTypeInfo?.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Specialities
                    </Typography>
                    <div className={classes.chipContainer}>
                      {establishmentDetail.specialitiesList?.length > 0 ? (
                        establishmentDetail.specialitiesList.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.name?.name}
                            className={classes.chip}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No specialities listed</Typography>
                      )}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Facilities
                    </Typography>
                    <div className={classes.chipContainer}>
                      {establishmentDetail.facilitiesList?.length > 0 ? (
                        establishmentDetail.facilitiesList.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.name?.name}
                            variant="outlined"
                            style={{ borderColor: "#dce7f2" }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No facilities listed</Typography>
                      )}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Brands
                    </Typography>
                    <div className={classes.chipContainer}>
                      {establishmentDetail.brandsList?.length > 0 ? (
                        establishmentDetail.brandsList.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.brandInfo?.name || "Unknown Brand"}
                            className={classes.chip}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No brands listed</Typography>
                      )}
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Services
                    </Typography>
                    <div className={classes.chipContainer}>
                      {establishmentDetail.servicesList?.length > 0 ? (
                        establishmentDetail.servicesList.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.name?.name || "Unknown Service"}
                            variant="outlined"
                            style={{ borderColor: "#dce7f2" }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No services listed</Typography>
                      )}
                    </div>
                  </Grid>
                  {/* 
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      Departments
                    </Typography>
                    <div className={classes.chipContainer}>
                      {establishmentDetail.departmentList?.length > 0 ? (
                        establishmentDetail.departmentList.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.departmentInfo?.name || `Department ID: ${item.proffession_id}`}
                            className={classes.chip}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No departments listed</Typography>
                      )}
                    </div>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* FULL WIDTH: GALLERY & BANNERS */}
          <Grid item xs={12}>
            {/* GALLERY SECTION */}
            <Card className={classes.card} style={{ marginBottom: "24px" }}>
              <CardContent>
                <Typography className={classes.sectionTitle}>
                  <ContactInfoIconWrapper icon={<InsertPhotoIcon />} />
                  Images
                </Typography>
                <Grid container spacing={2}>
                  {establishmentDetail.imageList?.length > 0 ? (
                    establishmentDetail.imageList.map((item, index) => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                        <img
                          src={item.image}
                          alt={`Gallery ${index + 1}`}
                          className={classes.galleryImage}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={4}
                        bgcolor="#f9fafb"
                        borderRadius="8px"
                      >
                        <InsertPhotoIcon style={{ fontSize: 40, color: '#919eab' }} />
                        <Typography variant="body2" color="textSecondary" mt={1}>
                          No images uploaded
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* BANNERS SECTION */}
            {establishmentDetail.bannerImageList?.length > 0 && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.sectionTitle}>
                    <ContactInfoIconWrapper icon={<InsertPhotoIcon />} />
                    Banners
                  </Typography>
                  <Grid container spacing={2}>
                    {establishmentDetail.bannerImageList.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <img
                          src={item.image}
                          alt={`Banner ${index + 1}`}
                          className={classes.galleryImage}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      ) : (
        <Card className={classes.card}>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography variant="h6" color="textSecondary">
                {user && !user.establishment_id ? "No Establishment Assigned to this User." : "Loading Establishment Details..."}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ---------------- CHANGE PASSWORD DIALOG ---------------- */}
      <Dialog
        open={openChangePass}
        onClose={() => setOpenChangePass(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: "12px" }
        }}
      >
        <DialogTitle style={{ borderBottom: "1px solid #eee", padding: "20px" }}>Change Password</DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <TextField
            margin="normal"
            label="Current Password"
            type={showPassword.current ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                currentPassword: e.target.value,
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        current: !showPassword.current,
                      })
                    }
                  >
                    {showPassword.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            label="New Password"
            type={showPassword.new ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                newPassword: e.target.value,
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        new: !showPassword.new,
                      })
                    }
                  >
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            label="Confirm Password"
            type={showPassword.confirm ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                confirmPassword: e.target.value,
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPassword({
                        ...showPassword,
                        confirm: !showPassword.confirm,
                      })
                    }
                  >
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={() => setOpenChangePass(false)} color="inherit" style={{ marginRight: "10px" }}>Cancel</Button>
          <Button
            className={`${classes.actionBtn} ${classes.primaryBtn}`}
            variant="contained"
            onClick={handlePasswordSubmit}
          >
            Save Password
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Helper components for cleaner code
const InfoRow = ({ icon, label, value, classes }) => (
  <div className={classes.infoItem}>
    <div className={classes.infoIcon}>{icon}</div>
    <div>
      <Typography className={classes.infoLabel}>{label}</Typography>
      <Typography className={classes.infoValue}>{value || "—"}</Typography>
    </div>
  </div>
);

const ContactInfoIconWrapper = ({ icon }) => (
  <span style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef4fa",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    color: "#255480",
    marginRight: "10px"
  }}>
    {React.cloneElement(icon, { style: { fontSize: "18px" } })}
  </span>
)

export default Profile;